import { useEffect, useRef } from 'react';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import { motion, useAnimation } from 'framer-motion';
import { Columns, Column } from '../columns';
import { Button } from '../button';
import { Vine } from '../vine';
import { AnimatedText } from '../animated-text';
import { useIO } from '../../hooks/use-io';
import { transitionSettings } from '../../lib/constants';

export const Story = ({ post, className, lastChild }) => {
  const { fields, excerpt: fallbackExcerpt, frontmatter } = post;
  const { slug } = fields;
  const {
    image: attachedImage,
    optionalImage,
    title,
    project,
    excerpt,
  } = frontmatter;
  const image = getImage(attachedImage);
  const image2 = getImage(optionalImage);

  const flagAnimation = useAnimation();
  const titleAnimation = useAnimation();
  const excerptAnimation = useAnimation();
  const buttonAnimation = useAnimation();

  const ref = useRef();
  const entry = useIO(ref, true);
  const isVisible = !!entry?.isIntersecting;

  useEffect(() => {
    if (isVisible) {
      // play animation
      flagAnimation.start(() => ({
        opacity: [0, 1],
      }));
      titleAnimation.start(() => ({
        opacity: [0, 1],
      }));
      excerptAnimation.start(() => ({
        opacity: [0, 1],
        y: [10, 0],
      }));
      buttonAnimation.start(() => ({
        opacity: [0, 1],
        y: [10, 0],
      }));
    }
  }, [isVisible]);

  return (
    <>
      <article className={['story', className].filter(Boolean).join(' ')}>
        <div ref={ref} className="story__trigger"></div>
        <Columns>
          <Column>
            <>
              {image2 ? (
                <div className="leaf-wrapper-group">
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{
                      opacity: isVisible ? 1 : 0,
                    }}
                    transition={{ ...transitionSettings }}
                    className="leaf-wrapper"
                  >
                    <GatsbyImage image={image} alt="" />
                  </motion.div>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{
                      opacity: isVisible ? 1 : 0,
                    }}
                    transition={{ ...transitionSettings, delay: 0.2 }}
                    className="leaf-wrapper"
                  >
                    <GatsbyImage image={image2} alt="" />
                  </motion.div>
                </div>
              ) : (
                image && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{
                      opacity: isVisible ? 1 : 0,
                    }}
                    transition={{ duration: 0.5 }}
                    className="leaf-wrapper"
                  >
                    <GatsbyImage image={image} alt="" />
                  </motion.div>
                )
              )}
            </>
          </Column>
          <Column>
            <motion.div
              initial={{ visibility: 'hidden' }}
              animate={{
                visibility: isVisible ? 'visible' : 'hidden',
              }}
            >
              <motion.span
                initial={{ opacity: 0 }}
                animate={flagAnimation}
                transition={{ ...transitionSettings, delay: 0 }}
                className="flag"
              >
                {project}
                <Vine flip={false} />
              </motion.span>
              <h2>
                <AnimatedText isVisible={isVisible}>{title}</AnimatedText>
              </h2>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={excerptAnimation}
                transition={{ ...transitionSettings, delay: 0.4 }}
              >
                {excerpt || fallbackExcerpt}
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={buttonAnimation}
                transition={{ ...transitionSettings, delay: 0.6 }}
              >
                <Button to={`/stories${slug}`}>Read More</Button>
              </motion.div>
            </motion.div>
          </Column>
        </Columns>
      </article>
      {!lastChild && <Vine isWide />}
    </>
  );
};
